// Slim shell for the option editor. All draft state lives in the optionEditor
// slice (open/close, fields, pricing), so this component just composes the
// fields and wires Save/Close. Save is a two-write thunk: option row first,
// then its pricing rule (keyed by the option id, which only exists after the
// row is created).
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert,
  FormControlLabel, Switch, Box, CircularProgress, Divider,
} from '@mui/material';
import {
  closeEditor, setDraftField, setDraftName, setTiers, setStrategy, setFlatAmount,
  saveOptionDraft,
} from '../../store/option-editor-slice.js';
import PricingStrategySection from './PricingStrategySection.jsx';

const OptionEditorDialog = () => {
  const dispatch = useDispatch();
  const { saving, saveError } = useSelector((state) => state.adminCatalog);
  const {
    open, optionId, draft, tiers, strategy, flatAmount,
  } = useSelector((state) => state.optionEditor);
  const isEdit = Boolean(optionId);

  const close = () => dispatch(closeEditor());

  const handleSubmit = async (event) => {
    event.preventDefault();
    const result = await dispatch(saveOptionDraft());
    if (result.ok) close();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle>{isEdit ? 'Edit option' : 'New option'}</DialogTitle>
        <DialogContent sx={{
          display: 'flex', flexDirection: 'column', gap: 2, pt: 2,
        }}
        >
          {saveError && <Alert severity="error">{saveError}</Alert>}
          <TextField
            label="Name"
            value={draft.name}
            onChange={(e) => dispatch(setDraftName(e.target.value))}
            required
            autoFocus
            slotProps={{ htmlInput: { maxLength: 200 } }}
          />
          <TextField
            label="Short description"
            value={draft.short_description || ''}
            onChange={(e) => dispatch(setDraftField({ field: 'short_description', value: e.target.value }))}
            multiline
            minRows={2}
          />
          <TextField
            label="Display order"
            type="number"
            value={draft.display_order}
            onChange={(e) => dispatch(setDraftField({ field: 'display_order', value: e.target.value }))}
            slotProps={{ htmlInput: { min: 0 } }}
          />
          <FormControlLabel
            control={(
              <Switch
                checked={draft.is_active}
                onChange={(e) => dispatch(setDraftField({ field: 'is_active', value: e.target.checked }))}
              />
            )}
            label="Active"
          />
          <Divider />
          <PricingStrategySection
            strategy={strategy}
            onStrategyChange={(next) => dispatch(setStrategy(next))}
            tiers={tiers}
            onTiersChange={(next) => dispatch(setTiers(next))}
            flatAmount={flatAmount}
            onFlatAmountChange={(next) => dispatch(setFlatAmount(next))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default OptionEditorDialog;
